 import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { useNavigate, useLocation } from "react-router-dom";
import DataTableMUI from "../../components/common/DataTable";
import KYCDocumentsForm from "./form";
import { eventColumns } from "../../services/tableColomn";
import ViewDataPopup from "./viewDataPopup";
import { deleteEventCategory, getAllEventCategories } from "../../redux/slices/eventCategorySlice";

const statusOptions = [
  { value: "all", label: "ALL", color: "#2563eb" },
  { value: "active", label: "ACTIVE", color: "#388e3c" },
  { value: "inactive", label: "INACTIVE", color: "#bfa100" },
  { value: "deleted", label: "DELETED", color: "#d32f2f" },
];

const EventCategory = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { eventCategory, loading, statusCounts, pagination } = useAppSelector(
    (state) => state.eventCategory
  );

  const [state, setState] = useState({
    showForm: false,
    editData: null,
    viewPopupOpen: false,
    selectedRow: null,
    statusFilter: "all",
    page: 1,
    itemsPerPage: 10,
    sortKey: "createdAt",
    sortOrder: "desc",
    search: "",
    searchInput: "",
  });

  const fetchData = React.useCallback(
    (params = {}) => {
      dispatch(
        getAllEventCategories({
          status: state.statusFilter,
          page: state.page,
          search: state.search,
          limit: state.itemsPerPage,
          sortKey: state.sortSort,
          sortOrder: state.sortOrder,
          ...params,
        })
      );
    },
    [
      dispatch,
      state.statusFilter,
      state.page,
      state.search,
      state.itemsPerPage,
      state.sortSort,
      state.sortOrder,
    ]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]); 

  useEffect(() => {
    if (location.state?.refresh) {
      fetchData({ page: 1 });
      window.history.replaceState({}, document.title);
    }
  }, [location.state, fetchData]); 

  const handlers = {
    add: () => navigate("/event-category-form"),
    edit: (row) =>
      navigate("/event-category-form", { state: { initialData: row } }),
    view: (row) =>
      setState((prev) => ({ ...prev, selectedRow: row, viewPopupOpen: true })),
    statusChange: (status) =>
      setState((prev) => ({ ...prev, statusFilter: status, page: 1 })),
    sort: (col) => {
      if (!col.sortable) return;
      setState((prev) => ({
        ...prev,
        sortKey: col.accessor,
        sortOrder:
          prev.sortKey === col.accessor && prev.sortOrder === "asc"
            ? "desc"
            : "asc",
        page: 1,
      }));
    },
    search: () =>
      setState((prev) => ({ ...prev, search: prev.searchInput, page: 1 })),
    closeView: () =>
      setState((prev) => ({
        ...prev,
        viewPopupOpen: false,
        selectedRow: null,
      })),
  };

  const handleDelete = async (row) => {
    if (window.confirm("Are you sure you want to delete this event category?")) {
      try {
        await dispatch(deleteEventCategory(row._id));
        fetchData(); 
      } catch (error) {
        console.error("Delete failed:", error);
      }
    }
  };

  const statusOpts = statusOptions.map((opt) => ({
    ...opt,
    count: statusCounts?.[opt.value] || 0,
  }));

  return (
    <div>
      {state.showForm ? (
        <KYCDocumentsForm
          onClose={() => {
            setState((prev) => ({ ...prev, showForm: false, editData: null }));
            fetchData({ page: 1 });
          }}
          initialData={state.editData}
        />
      ) : (
        <div className="data-table">
          <DataTableMUI
            title="Event Category"
            columns={eventColumns}
            data={eventCategory || []}
            statusOptions={statusOpts}
            statusFilter={state.statusFilter}
            onStatusFilterChange={handlers.statusChange}
            onView={handlers.view}
            onEdit={handlers.edit}
            onDelete={handleDelete}
            onAdd
            onAddClick={handlers.add}
            pagination
            itemsPerPage={state.itemsPerPage}
            currentPage={pagination?.page || 1}
            totalItems={pagination?.total || 0}
            setItemsPerPage={(value) =>
              setState((prev) => ({ ...prev, itemsPerPage: value }))
            }
            onPageChange={(newPage) =>
              setState((prev) => ({ ...prev, page: newPage }))
            }
            searchable
            searchTerm={state.searchInput}
            setSearchTerm={(value) =>
              setState((prev) => ({ ...prev, searchInput: value }))
            }
            onSearch={handlers.search}
            loading={loading}
            sortKey={state.sortKey}
            sortOrder={state.sortOrder}
            onSort={handlers.sort}
            draggable={true}
          />

          {state.selectedRow && (
            <ViewDataPopup
              open={state.viewPopupOpen}
              onClose={handlers.closeView}
              data={state.selectedRow}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default EventCategory;
